import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, ArrowRight, ExternalLink, TrendingUp, TrendingDown, BarChart2, AlertTriangle } from 'lucide-react';
import { getWalletTradesWithPagination, getWalletPnLDetailed } from '../services/solanatrackerService';

// Types
interface WalletTradeHistoryProps {
  walletAddress: string;
  maxHeight?: string;
  onRefresh?: () => void;
}

interface Trade {
  tx: string;
  from: TokenInfo;
  to: TokenInfo;
  price: {
    usd: number;
    sol: string;
  };
  volume: {
    usd: number;
    sol: number;
  };
  wallet: string;
  program: string;
  time: number;
}

interface TokenInfo {
  address: string;
  amount: number;
  token: {
    name: string;
    symbol: string;
    image: string;
    decimals: number;
  };
  priceUsd: number;
}

interface Position {
  token: {
    mint: string;
    name: string;
    symbol: string;
    image: string;
  };
  amount: number;
  value: {
    usd: number;
    sol: number;
  };
  pnl: {
    usd: number;
    sol: number;
    percentage: number;
  };
  invested: {
    usd: number;
    sol: number;
  };
}

// Helper utility to determine trade type
const determineTradeType = (trade: Trade): 'buy' | 'sell' | 'swap' => {
  // SOL address for identifying buys/sells
  const SOL_ADDRESS = 'So11111111111111111111111111111111111111112';
  
  if (trade.from.address === SOL_ADDRESS && trade.to.address !== SOL_ADDRESS) {
    return 'buy';
  } else if (trade.from.address !== SOL_ADDRESS && trade.to.address === SOL_ADDRESS) {
    return 'sell';
  } else {
    return 'swap';
  }
};

// Format currency amounts
const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}K`;
  } else if (amount >= 1) {
    return `$${amount.toFixed(2)}`;
  } else {
    return `$${amount.toFixed(6)}`;
  }
};

// Format token amounts
const formatTokenAmount = (amount: number): string => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}K`;
  } else if (amount >= 1) {
    return amount.toFixed(2);
  } else {
    return amount.toFixed(6);
  }
};

// Format time from timestamp
const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  } else if (diffSeconds < 3600) {
    return `${Math.floor(diffSeconds / 60)}m ago`;
  } else if (diffSeconds < 86400) {
    return `${Math.floor(diffSeconds / 3600)}h ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Render appropriate icon for trade type
const getTradeIcon = (tradeType: 'buy' | 'sell' | 'swap') => {
  switch (tradeType) {
    case 'buy':
      return <TrendingUp size={14} className="text-green-500" />;
    case 'sell':
      return <TrendingDown size={14} className="text-red-500" />;
    case 'swap':
      return <ArrowRight size={14} className="text-purple-500" />;
  }
};

// Get color class for PnL
const getPnLColorClass = (pnlValue: number) => {
  if (pnlValue > 0) return 'text-green-500';
  if (pnlValue < 0) return 'text-red-500';
  return 'text-gray-400';
};

const WalletTradeHistory: React.FC<WalletTradeHistoryProps> = ({
  walletAddress,
  maxHeight = '300px',
  onRefresh
}) => {
  const [trades, setTrades] = useState<Trade[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'trades' | 'positions'>('trades');

  // Fetch trades with pagination
  const fetchTrades = useCallback(async () => {
    if (!walletAddress) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getWalletTradesWithPagination(walletAddress, 20, page);
      
      if (response.status === 'error') {
        throw new Error(response.error || 'Failed to fetch trades');
      }
      
      if (response.data) {
        setTrades(prevTrades => 
          page === 1 ? response.data.trades : [...prevTrades, ...response.data.trades]
        );
        setHasMore(response.data.hasMore || false);
      }
    } catch (err) {
      console.error('Error fetching trades:', err);
      setError(err instanceof Error ? err.message : 'Failed to load trades');
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress, page]);

  // Fetch profit and loss data
  const fetchPnL = useCallback(async () => {
    if (!walletAddress) return;
    
    setPnlLoading(true);
    
    try {
      const response = await getWalletPnLDetailed(walletAddress);
      
      if (response.status === 'error' || !response.data) {
        console.error('Error fetching PnL:', response.error);
        return;
      }
      
      // Extract positions from the response
      if (response.data.positions && Array.isArray(response.data.positions)) {
        setPositions(response.data.positions);
      }
    } catch (err) {
      console.error('Error fetching PnL data:', err);
    } finally {
      setPnlLoading(false);
    }
  }, [walletAddress]);

  // Load initial data
  useEffect(() => {
    if (walletAddress) {
      setPage(1);
      fetchTrades();
      fetchPnL();
    }
  }, [walletAddress, fetchTrades, fetchPnL]);

  // Handle load more
  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  // Handle refresh
  const handleRefresh = () => {
    setPage(1);
    fetchTrades();
    fetchPnL();
    if (onRefresh) onRefresh();
  };
  
  // Calculate total P&L from positions
  const totalPnL = useMemo(() => {
    return positions.reduce((sum, position) => sum + (position.pnl?.usd || 0), 0);
  }, [positions]);
  
  // Format P&L amount with sign
  const formatPnL = (amount: number): string => {
    return amount >= 0 ? `+${formatCurrency(amount)}` : formatCurrency(amount);
  };
  
  // Format P&L percentage
  const formatPnLPercentage = (percentage: number): string => {
    return percentage >= 0 ? `+${percentage.toFixed(2)}%` : `${percentage.toFixed(2)}%`;
  };

  return (
    <div className="flex flex-col h-full rounded-lg overflow-hidden backdrop-blur-sm bg-dark-grey/40 border border-gray-700/30">
      {/* Header */}
      <div className="p-4 border-b border-gray-700/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart2 size={16} className="text-[#47be8f]" />
            <h3 className="text-sm font-semibold text-white/90">
              {walletAddress ? `Wallet Activity` : 'No Wallet Selected'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab selector */}
            <div className="flex text-xs border border-gray-700/40 rounded-md overflow-hidden">
              <button
                onClick={() => setActiveTab('trades')}
                className={`px-3 py-1 ${
                  activeTab === 'trades' 
                    ? 'bg-[#47be8f]/20 text-[#47be8f]' 
                    : 'bg-dark-grey/30 text-gray-400 hover:text-white hover:bg-gray-700/20'
                } transition-colors`}
              >
                Trades
              </button>
              <button
                onClick={() => setActiveTab('positions')}
                className={`px-3 py-1 ${
                  activeTab === 'positions' 
                    ? 'bg-[#47be8f]/20 text-[#47be8f]' 
                    : 'bg-dark-grey/30 text-gray-400 hover:text-white hover:bg-gray-700/20'
                } transition-colors`}
              >
                Positions
              </button>
            </div>
            <button 
              onClick={handleRefresh} 
              className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/30 transition-colors"
              title="Refresh data"
            >
              <RefreshCw size={14} className={isLoading || pnlLoading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Trade list with scrolling */}
      <div 
        className="overflow-y-auto custom-scrollbar flex-grow"
        style={{ maxHeight: maxHeight }}
      >
        {activeTab === 'trades' ? (
          <>
            {/* Trades Tab */}
            {isLoading && trades.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mb-2" />
                <p className="text-sm text-gray-400 mb-1">{error}</p>
                <button 
                  className="mt-2 px-3 py-1 text-xs bg-[#47be8f]/20 text-[#47be8f] rounded-md hover:bg-[#47be8f]/30 transition-colors"
                  onClick={handleRefresh}
                >
                  Try Again
                </button>
              </div>
            ) : trades.length === 0 ? (
              <div className="text-center p-8">
                <p className="text-sm text-gray-400">No trades found for this wallet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700/30">
                {trades.map((trade, index) => {
                  const tradeType = determineTradeType(trade);
                  return (
                    <div key={index} className="p-4 hover:bg-dark-grey/50 transition-colors">
                      {/* Trade Row */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          {/* Icon */}
                          <div className={`p-2 rounded-full ${
                            tradeType === 'buy' ? 'bg-green-500/10' : 
                            tradeType === 'sell' ? 'bg-red-500/10' :
                            'bg-purple-500/10'
                          }`}>
                            {getTradeIcon(tradeType)}
                          </div>
                          
                          {/* Trade info */}
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-gray-200 capitalize">
                                {tradeType}
                              </p>
                              <p className="text-xs text-gray-400">{formatTime(trade.time)}</p>
                            </div>
                            <div className="flex items-center mt-1">
                              {/* Token images */}
                              <div className="flex items-center gap-1">
                                <div className="relative">
                                  <img 
                                    src={trade.from.token.image} 
                                    alt={trade.from.token.symbol}
                                    className="w-4 h-4 rounded-full"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png';
                                    }}
                                  />
                                </div>
                                <ArrowRight size={10} className="text-gray-500" />
                                <div className="relative">
                                  <img 
                                    src={trade.to.token.image}
                                    alt={trade.to.token.symbol}
                                    className="w-4 h-4 rounded-full"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png';
                                    }}
                                  />
                                </div>
                              </div>
                              <p className="ml-2 text-xs text-gray-400">
                                {tradeType === 'buy' 
                                  ? `${formatTokenAmount(trade.to.amount)} ${trade.to.token.symbol}`
                                  : tradeType === 'sell'
                                  ? `${formatTokenAmount(trade.from.amount)} ${trade.from.token.symbol}`
                                  : `${trade.from.token.symbol} → ${trade.to.token.symbol}`
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* USD Value */}
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-200">
                            {formatCurrency(trade.volume.usd)}
                          </p>
                          <a 
                            href={`https://solscan.io/tx/${trade.tx}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-gray-400 flex items-center justify-end gap-1 hover:text-[#47be8f] hover:underline"
                          >
                            View <ExternalLink size={10} />
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Load more button */}
                {hasMore && (
                  <div className="p-3 border-t border-gray-700/30">
                    <button 
                      onClick={handleLoadMore}
                      className="w-full py-1.5 text-xs text-gray-300 hover:text-white transition-colors"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-3 h-3 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        'Load More'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Positions Tab */}
            {pnlLoading && positions.length === 0 ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : positions.length === 0 ? (
              <div className="text-center p-8">
                <p className="text-sm text-gray-400">No positions found for this wallet</p>
              </div>
            ) : (
              <>
                {/* PnL Summary */}
                <div className="p-3 border-b border-gray-700/30">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">Total P&L:</p>
                    <p className={`text-sm font-semibold ${getPnLColorClass(totalPnL)}`}>
                      {formatPnL(totalPnL)}
                    </p>
                  </div>
                </div>
                
                {/* Positions List */}
                <div className="divide-y divide-gray-700/30">
                  {positions.map((position, index) => (
                    <div key={index} className="p-4 hover:bg-dark-grey/50 transition-colors">
                      <div className="flex justify-between items-center">
                        {/* Token info */}
                        <div className="flex items-center gap-3">
                          <img 
                            src={position.token.image} 
                            alt={position.token.symbol}
                            className="w-8 h-8 rounded-full"
                            onError={(e) => {
                              e.currentTarget.src = 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png';
                            }}
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-200">
                              {position.token.symbol}
                            </p>
                            <p className="text-xs text-gray-400">
                              {formatTokenAmount(position.amount)}
                            </p>
                          </div>
                        </div>
                        
                        {/* PnL info */}
                        <div className="text-right">
                          <p className={`text-sm font-medium ${getPnLColorClass(position.pnl?.usd || 0)}`}>
                            {formatPnL(position.pnl?.usd || 0)}
                          </p>
                          <p className={`text-xs ${getPnLColorClass(position.pnl?.percentage || 0)}`}>
                            {formatPnLPercentage(position.pnl?.percentage || 0)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Value info */}
                      <div className="mt-2 grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="text-gray-500">Current Value</p>
                          <p className="text-gray-300">{formatCurrency(position.value?.usd || 0)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Invested</p>
                          <p className="text-gray-300">{formatCurrency(position.invested?.usd || 0)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WalletTradeHistory; 